import * as React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Loader2, MessageSquare, Repeat, Sparkles, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import SearchableSelect from '@/components/common/SearchableSelect';
import { AdvisorAvatar, DirectoryEntry } from '@/components/advisor/public/AdvisorCardGrid';
import AdvisorProfileDetail, { type PublicOfferSummary } from '@/components/advisor/public/AdvisorProfileDetail';
import PackageCard from '@/components/offers/PackageCard';
import { ADVISOR_CHANGE_REASONS, type AdvisorChangeReason, type AdvisorSwitch } from '@/types/chat';

export type CurrentAdvisor = { id: string; display_name: string } | null;

interface Props {
  advisor: DirectoryEntry;
  current: CurrentAdvisor;
  busy: boolean;
  blockedBy: string | null;
  onChoose: (switching: AdvisorSwitch | null) => void;
  onRequest: (offer: PublicOfferSummary, packageId: string, switching: AdvisorSwitch | null) => void;
  onClose: () => void;
  onTalk: () => void;
}

export default function AdvisorProfilePanel({
  advisor, current, busy, blockedBy, onChoose, onRequest, onClose, onTalk,
}: Props) {
  const [offers, setOffers] = React.useState<PublicOfferSummary[]>([]);
  const [reason, setReason] = React.useState<AdvisorChangeReason | ''>('');
  const [comment, setComment] = React.useState('');
  const first = advisor.display_name.split(' ')[0];
  const isCurrent = current?.id === advisor.id;
  const switching = Boolean(current) && !isCurrent;
  const reasonReady = Boolean(reason) && (reason !== 'other' || comment.trim().length > 0);
  const canAct = !busy && (!switching || reasonReady);
  const details: AdvisorSwitch | null = switching ? { reason: reason as AdvisorChangeReason, comment } : null;

  return (
    <aside
      className="flex max-h-[calc(100vh-8rem)] flex-col overflow-hidden rounded-xl border bg-card shadow-sm lg:sticky lg:top-6"
      data-testid="advisor-profile-panel"
    >
      <div className="border-b bg-gradient-to-br from-amber-50 to-background p-5">
        <div className="flex items-start gap-4">
          <AdvisorAvatar advisor={advisor} size="lg" />
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-semibold leading-tight">{advisor.display_name}</h2>
            {advisor.headline && <p className="mt-1 text-sm text-muted-foreground">{advisor.headline}</p>}
          </div>
          <Button variant="ghost" size="icon" className="-mr-2 -mt-2 h-8 w-8 shrink-0" onClick={onClose} aria-label="Close profile">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {isCurrent && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground" data-testid="find-advisor-current">Already your advisor.</span>
            <Button size="sm" variant="outline" onClick={onTalk}>
              <MessageSquare className="mr-2 h-4 w-4" />
              Talk to them
            </Button>
          </div>
        )}

        {!current && (
          <div className="mt-4">
            <Button onClick={() => onChoose(null)} disabled={busy} data-testid="find-advisor-confirm">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
              Work with {first}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        )}

        {switching && current && (
          <div className="mt-4 space-y-3" data-testid="change-advisor-form">
            <p className="text-sm">
              You work with <strong>{current.display_name}</strong> today. Switching to {first} keeps that
              conversation readable and opens a new one with {first}.
            </p>
            {blockedBy && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950" data-testid="change-advisor-blocked">
                <p>{blockedBy}</p>
                <Link to="/contracts" className="mt-1 inline-block font-medium underline underline-offset-2">Open contracts</Link>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Why are you switching?</Label>
              <SearchableSelect
                value={reason}
                onChange={(value) => setReason(value as AdvisorChangeReason)}
                placeholder="Choose a reason…"
                searchPlaceholder="Search reasons…"
                options={ADVISOR_CHANGE_REASONS.map((option) => ({ value: option.value, label: option.label }))}
                testId="change-advisor-reason"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="change-comment">
                Anything else? {reason === 'other' && <span className="text-red-600">Required</span>}
              </Label>
              <Textarea
                id="change-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={2}
                placeholder="What would have made this work better?"
                data-testid="change-advisor-comment"
              />
              <p className="text-xs text-muted-foreground">Only BitSwan sees this. Your outgoing advisor does not.</p>
            </div>
            <Button onClick={() => onChoose(details)} disabled={!canAct} data-testid="change-advisor-submit">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Repeat className="mr-2 h-4 w-4" />}
              Switch to {first}
            </Button>
          </div>
        )}
      </div>

      <div className="overflow-y-auto p-5">
        {offers.length > 0 && (
          <section className="mb-6 space-y-3" data-testid="panel-public-offers">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Ready-made offers</h4>
            <p className="text-sm text-muted-foreground">
              Start with a package {first} already priced. {switching ? `Give your reason above first; you switch to ${first} and they confirm it` : `You become their client and they confirm it`}, or tell you why not, in your conversation.
            </p>
            {offers.map((offer) => (
              <div key={offer.id} className="space-y-2 rounded-lg border p-3" data-testid={`panel-offer-${offer.id}`}>
                <div>
                  <p className="font-medium">{offer.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {offer.billing_mode === 'via_aoc' ? 'On your monthly BitSwan invoice' : `Invoiced by ${first} directly`} · {offer.currency}, excl. VAT
                  </p>
                  {offer.introduction && <p className="mt-1 text-sm text-muted-foreground">{offer.introduction}</p>}
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {offer.packages.map((pkg) => (
                    <div key={pkg.id} className="space-y-2">
                      <PackageCard pkg={pkg} currency={offer.currency} />
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full"
                        disabled={!canAct}
                        onClick={() => onRequest(offer, pkg.id!, details)}
                        data-testid={`request-package-${pkg.id}`}
                      >
                        {busy ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : null}
                        Start with {pkg.name}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </section>
        )}
        <AdvisorProfileDetail
          profileId={advisor.id}
          languages={advisor.languages}
          country={advisor.country}
          cities={advisor.cities}
          onOffers={setOffers}
        />
      </div>
    </aside>
  );
}
